import { apiRequest } from '@/lib/api/client'
import { landingApiRequest } from '@/lib/landing-api'
import type { LandingRouletteHistoryItem } from '@/lib/landing-roulette'

export type ScratchOutcome = {
  type: 'lucky_number' | 'coupon' | 'none'
  label: string
  probability: number
  quantity: number | null
  discount_percent: number | null
}

export type ScratchCardConfig = {
  enabled: boolean
  no_prize_message: string
  outcomes: ScratchOutcome[]
}

export const DEFAULT_SCRATCH_CONFIG: ScratchCardConfig = {
  enabled: false,
  no_prize_message:
    'Desta vez não veio um prêmio. Seu apoio continua fazendo a diferença!',
  outcomes: [
    {
      type: 'none',
      label: 'Não foi desta vez',
      probability: 100,
      quantity: null,
      discount_percent: null,
    },
  ],
}

export type ScratchCardAttempt = {
  id: number
  revealed_at: string | null
  created_at: string
  won: boolean
  title: string
  text: string | null
  video_url: string | null
  prize: LandingRouletteHistoryItem
}

export type ScratchCardState = {
  enabled: boolean
  available: boolean
  message: string
  invitation: string
  attempt: ScratchCardAttempt | null
}

export type ScratchRecipients = {
  items: {
    id: number
    created_at: string
    revealed_at: string | null
    donor: { id: number; name: string; email: string | null; phone: string | null } | null
    prize: LandingRouletteHistoryItem
  }[]
  pagination: { current_page: number; last_page: number; total: number; per_page: number }
}

export function playCampaignScratchCard(campaignId: number) {
  return landingApiRequest<ScratchCardAttempt>(
    `/api/me/campaigns/${campaignId}/scratch-card/play`,
    { method: 'POST' }
  )
}

export function revealCampaignScratchCard(campaignId: number) {
  return landingApiRequest<ScratchCardAttempt>(
    `/api/me/campaigns/${campaignId}/scratch-card/reveal`,
    { method: 'POST' }
  )
}

export function fetchScratchRecipients(
  campaignId: number,
  filters: URLSearchParams,
  token?: string,
  signal?: AbortSignal
) {
  return apiRequest<ScratchRecipients>(
    `/api/campaigns/${campaignId}/gamification/scratch-card/recipients?${filters}`,
    { token, signal, requireAuth: true, cache: 'no-store' }
  )
}

export function scratchProbabilityTotal(outcomes: ScratchOutcome[]): number {
  return (
    outcomes.reduce((sum, outcome) => sum + Math.round(outcome.probability * 100), 0) /
    100
  )
}

export function validateScratchConfig(config: ScratchCardConfig): string | null {
  if (!config.no_prize_message.trim()) return 'Escreva a mensagem para quem não ganhar.'
  if (config.outcomes.length < 1 || config.outcomes.length > 20)
    return 'Configure de 1 a 20 resultados.'
  for (const outcome of config.outcomes) {
    if (!outcome.label.trim()) return 'Dê um nome a cada resultado.'
    if (
      !Number.isFinite(outcome.probability) ||
      outcome.probability < 0 ||
      outcome.probability > 100 ||
      Math.abs(outcome.probability * 100 - Math.round(outcome.probability * 100)) >
        0.000001
    )
      return 'Use probabilidades de 0 a 100%, com até duas casas decimais.'
    if (
      outcome.type === 'lucky_number' &&
      (!Number.isInteger(outcome.quantity) ||
        Number(outcome.quantity) < 1 ||
        Number(outcome.quantity) > 1000)
    )
      return 'Informe de 1 a 1.000 números extras por prêmio.'
    if (
      outcome.type === 'coupon' &&
      (!Number.isFinite(outcome.discount_percent) ||
        Number(outcome.discount_percent) < 0.01 ||
        Number(outcome.discount_percent) > 100)
    )
      return 'Informe um desconto de 0,01% a 100%.'
  }
  return scratchProbabilityTotal(config.outcomes) === 100
    ? null
    : 'As probabilidades, incluindo sem prêmio, devem somar exatamente 100%.'
}
